import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/apiService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CLOCK_SIZES = {
  small: 'text-[10vw]',
  medium: 'text-[12vw]',
  large: 'text-[14vw]',
  xlarge: 'text-[16vw]'
};

const DATE_SIZES = {
  small: 'text-[2.5vw]',
  medium: 'text-[3vw]',
  large: 'text-[4vw]',
  xlarge: 'text-[5vw]'
};

const LOGO_SIZES = {
  small: 'max-w-[8vw] max-h-[8vh]',
  medium: 'max-w-[12vw] max-h-[12vh]',
  large: 'max-w-[18vw] max-h-[18vh]'
};

const LOGO_POSITIONS = {
  'top-left': 'top-6 left-6',
  'top-right': 'top-6 right-6',
  'bottom-left': 'bottom-6 left-6',
  'bottom-right': 'bottom-6 right-6'
};

const EFFECT_STYLES = {
  glow_pulse: {
    initial: { opacity: 1 },
    animate: { 
      opacity: [1, 0.85, 1]
    },
    transition: { duration: 3, ease: 'easeInOut' }
  },
  stadium_flash: {
    initial: { backgroundColor: 'transparent' },
    animate: { 
      backgroundColor: ['transparent', '#FFFFFF', 'transparent', '#FFFFFF', 'transparent']
    },
    transition: { duration: 1.5, ease: 'easeInOut' }
  },
  cinematic_sweep: {
    initial: { opacity: 0, x: '-100%' },
    animate: { opacity: [0, 1, 1, 0], x: ['-100%', '0%', '0%', '100%'] },
    transition: { duration: 2, ease: 'easeInOut' }
  },
  energetic_burst: {
    initial: { scale: 1 },
    animate: { scale: [1, 1.1, 1, 1.05, 1] },
    transition: { duration: 2, ease: 'easeInOut' }
  }
};

function formatTime(date, format) {
  const hours24 = date.getHours();
  const hours12 = hours24 % 12 || 12;
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  const ampm = hours24 >= 12 ? 'PM' : 'AM';
  
  if (format === '12h') {
    return { time: `${hours12}:${minutes}:${seconds}`, suffix: ampm };
  }
  return { time: `${hours24.toString().padStart(2, '0')}:${minutes}:${seconds}`, suffix: null };
}

function formatDate(date, format) {
  const day = date.getDate();
  const dayPadded = day.toString().padStart(2, '0');
  const month = date.getMonth() + 1;
  const monthPadded = month.toString().padStart(2, '0');
  const year = date.getFullYear();
  
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  switch (format) {
    case 'DD/MM/YYYY':
      return `${dayPadded}/${monthPadded}/${year}`;
    case 'D MMMM YYYY':
      return `${day} ${monthNames[date.getMonth()]} ${year}`;
    case 'dddd D MMMM YYYY':
      return `${dayNames[date.getDay()]}, ${day} ${monthNames[date.getMonth()]} ${year}`;
    default:
      return `${dayPadded}/${monthPadded}/${year}`;
  }
}

// Digital Clock Component (LED Style)
function DigitalClock({ time, suffix, color, size }) {
  return (
    <div className="flex items-baseline justify-center font-mono whitespace-nowrap max-w-[98vw]" data-testid="digital-clock">
      <div 
        className={`${CLOCK_SIZES[size]} leading-none tracking-wider font-bold`}
        style={{ 
          color: color,
          fontFamily: "'DSEG7 Classic', 'Courier New', monospace",
          letterSpacing: '0.05em'
        }}
      >
        {time}
      </div>
      {suffix && (
        <span 
          className="text-[3vw] ml-4 font-mono opacity-80"
          style={{ color: color }}
        >
          {suffix}
        </span>
      )}
    </div>
  );
}

// Minimal Clock Component (Thin elegant)
function MinimalClock({ time, suffix, color, size }) {
  return (
    <div className="flex items-baseline justify-center whitespace-nowrap max-w-[98vw]" data-testid="minimal-clock">
      <span 
        className={`${CLOCK_SIZES[size]} leading-none tracking-tight font-extralight`}
        style={{ 
          color: color,
          fontFamily: "'Manrope', 'Helvetica Neue', sans-serif",
        }}
      >
        {time}
      </span>
      {suffix && (
        <span 
          className="text-[3vw] ml-4 font-light opacity-70"
          style={{ color: color }}
        >
          {suffix}
        </span>
      )}
    </div>
  );
}

// Modern Clock Component (Bold - default)
function ModernClock({ time, suffix, color, size, fontFamily }) {
  const sizeClasses = CLOCK_SIZES;

  return (
    <div className="flex items-baseline justify-center whitespace-nowrap max-w-[98vw]" data-testid="clock-display">
      <span 
        className={`${sizeClasses[size]} leading-none tracking-tighter font-bold`}
        style={{ color: color, fontFamily: `'${fontFamily}', 'Bebas Neue', sans-serif` }}
      >
        {time}
      </span>
      {suffix && (
        <span 
          className="text-[4vw] ml-4 font-bold opacity-80"
          style={{ color: color }}
        >
          {suffix}
        </span>
      )}
    </div>
  );
}

export default function ScreenDisplay() {
  const { id } = useParams();
  const screenNumber = parseInt(id, 10);
  
  const [config, setConfig] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isEffectActive, setIsEffectActive] = useState(false);
  const [lastEffectHour, setLastEffectHour] = useState(-1);
  const [error, setError] = useState(null);

  // Fetch config with polling
  // Fetch config initially and listen for real-time updates
  const fetchConfig = useCallback(async () => {
    try {
      const data = await api.getScreen(screenNumber);
      setConfig(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch screen config:', err);
      setError('Failed to load screen configuration');
    }
  }, [screenNumber]);

  useEffect(() => {
    fetchConfig();
    // Listen for real-time Firebase updates
    const unsubscribe = api.onScreenChange(screenNumber, (data) => {
      if (data) {
        setConfig(data);
        setError(null);
      }
    });
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [fetchConfig, screenNumber]);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      // Convert to Dubai time
      const dubaiTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Dubai' }));
      setCurrentTime(dubaiTime);
      
      // Check for hourly effect
      if (config?.effect_enabled && dubaiTime.getMinutes() === 0 && dubaiTime.getSeconds() === 0) {
        const currentHour = dubaiTime.getHours();
        if (currentHour !== lastEffectHour) {
          setLastEffectHour(currentHour);
          setIsEffectActive(true);
          setTimeout(() => setIsEffectActive(false), 3000);
        }
      }
    }, 1000);
    
    return () => clearInterval(timer);
  }, [config?.effect_enabled, lastEffectHour]);

  // Hide cursor after inactivity
  useEffect(() => {
    let timeout;
    const hideCursor = () => {
      document.body.style.cursor = 'none';
    };
    const showCursor = () => {
      document.body.style.cursor = 'default';
      clearTimeout(timeout);
      timeout = setTimeout(hideCursor, 3000);
    };
    
    document.addEventListener('mousemove', showCursor);
    timeout = setTimeout(hideCursor, 3000);
    
    return () => {
      document.removeEventListener('mousemove', showCursor);
      clearTimeout(timeout);
      document.body.style.cursor = 'default';
    };
  }, []);

  // Auto-refresh page every 30 minutes to pick up code updates
  useEffect(() => {
    var refreshInterval = setInterval(function() {
      window.location.reload();
    }, 30 * 60 * 1000);

    // Listen for admin-triggered forced refresh via Firebase
    var unsubRefresh = api.onRefreshSignal(function() {
      window.location.reload();
    });

    return () => {
      clearInterval(refreshInterval);
      if (typeof unsubRefresh === 'function') unsubRefresh();
    };
  }, []);

  if (error) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-[#0A0A0A] text-white font-['Anton']">
        <p className="text-4xl">{error}</p>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-[#0A0A0A] text-white">
        <div className="text-4xl font-['Anton'] animate-pulse">LOADING...</div>
      </div>
    );
  }

  const { time, suffix } = formatTime(currentTime, config.time_format);
  const dateStr = formatDate(currentTime, config.date_format);
  const effectStyle = EFFECT_STYLES[config.effect_style] || EFFECT_STYLES.glow_pulse;
  const clockStyle = config.clock_style || 'modern';

  const renderClock = () => {
    switch (clockStyle) {
      case 'digital':
        return <DigitalClock time={time} suffix={suffix} color={config.text_color} size={config.clock_size} />;
      case 'minimal':
        return <MinimalClock time={time} suffix={suffix} color={config.text_color} size={config.clock_size} />;
      default:
        return <ModernClock time={time} suffix={suffix} color={config.text_color} size={config.clock_size} fontFamily={config.font_family} />;
    }
  };

  return (
    <div 
      className="h-screen w-screen overflow-hidden flex flex-col items-center justify-center relative select-none"
      style={{ 
        backgroundColor: config.background_color,
        fontFamily: `'${config.font_family}', 'Bebas Neue', sans-serif`
      }}
      data-testid="screen-display"
    >
      {/* Logo */}
      {config.logo_url && config.logo_position !== 'hidden' && (
        <img
          src={config.logo_url.startsWith('/') ? `${API_URL}${config.logo_url}` : config.logo_url}
          alt="Logo"
          className={`absolute z-20 object-contain ${LOGO_POSITIONS[config.logo_position]} ${LOGO_SIZES[config.logo_size]}`}
          data-testid="screen-logo"
        />
      )}

      {/* Announcement Mode */}
      <AnimatePresence>
        {config.message_enabled && config.message_text && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0 z-50 bg-black flex items-center justify-center overflow-hidden"
            data-testid="announcement-overlay"
          >
            {/* Static Message */}
            {(config.message_animation === 'static' || !config.message_animation) && (
              <p 
                className="text-[8vw] leading-tight tracking-tight uppercase font-bold text-white p-12 text-center"
                style={{ fontFamily: `'${config.font_family}', 'Bebas Neue', sans-serif` }}
                data-testid="announcement-text"
              >
                {config.message_text}
              </p>
            )}

            {/* Scrolling Marquee Message */}
            {config.message_animation === 'scroll' && (
              <div className="w-full overflow-hidden">
                <motion.p 
                  className="text-[8vw] leading-tight tracking-tight uppercase font-bold text-white whitespace-nowrap"
                  style={{ fontFamily: `'${config.font_family}', 'Bebas Neue', sans-serif` }}
                  initial={{ x: '100%' }}
                  animate={{ x: '-100%' }}
                  transition={{ 
                    duration: Math.max(10, config.message_text.length * 0.3),
                    repeat: Infinity,
                    ease: 'linear'
                  }}
                  data-testid="announcement-text-scroll"
                >
                  {config.message_text}
                </motion.p>
              </div>
            )}

            {/* Blinking Message */}
            {config.message_animation === 'blink' && (
              <motion.p 
                className="text-[8vw] leading-tight tracking-tight uppercase font-bold text-white p-12 text-center"
                style={{ fontFamily: `'${config.font_family}', 'Bebas Neue', sans-serif` }}
                animate={{ opacity: [1, 0, 1] }}
                transition={{ 
                  duration: 1,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }}
                data-testid="announcement-text-blink"
              >
                {config.message_text}
              </motion.p>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hourly Effect Overlay */}
      <AnimatePresence>
        {isEffectActive && config.effect_style === 'cinematic_sweep' && (
          <motion.div
            key="cinematic"
            initial={effectStyle.initial}
            animate={effectStyle.animate}
            transition={effectStyle.transition}
            className="absolute inset-0 z-40 bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none"
          />
        )}
        {isEffectActive && config.effect_style === 'stadium_flash' && (
          <motion.div
            key="flash"
            initial={effectStyle.initial}
            animate={effectStyle.animate}
            transition={effectStyle.transition}
            className="absolute inset-0 z-40 pointer-events-none"
          />
        )}
      </AnimatePresence>

      {/* Clock Display */}
      <motion.div 
        className="z-10 flex flex-col items-center justify-center"
        animate={isEffectActive && (config.effect_style === 'glow_pulse' || config.effect_style === 'energetic_burst') ? effectStyle.animate : {}}
        transition={effectStyle.transition}
        style={{}}
      >
        {renderClock()}
        
        {config.show_date && (
          <p 
            className={`${DATE_SIZES[config.clock_size]} mt-4 tracking-wide opacity-90`}
            style={{ 
              color: config.text_color,
              fontFamily: clockStyle === 'digital' ? "'DSEG7 Classic', 'Courier New', monospace" : clockStyle === 'minimal' ? "'Manrope', sans-serif" : `'${config.font_family}', sans-serif`
            }}
            data-testid="date-display"
          >
            {dateStr}
          </p>
        )}
      </motion.div>

      {/* Screen Number Indicator (subtle) */}
      <div 
        className="absolute bottom-4 right-4 text-xs opacity-20 z-10"
        style={{ color: config.text_color }}
      >
        SCREEN {screenNumber}
      </div>
    </div>
  );
}
